import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import client from "../lib/api";
import { Lock, Check, Clock, Flame, ChevronRight } from "lucide-react";

export default function Challenges() {
    const [levels, setLevels] = useState([]);
    const [active, setActive] = useState(1);

    useEffect(() => {
        client.get("/levels").then((r) => {
            setLevels(r.data);
            const firstUnlocked = r.data.find((l) => l.unlocked && !l.completed) || r.data[0];
            if (firstUnlocked) setActive(firstUnlocked.level);
        });
    }, []);

    const activeLevel = levels.find((l) => l.level === active);

    return (
        <div className="px-5 pt-8">
            <span className="label-cap text-xs text-neon-orange">Challenges</span>
            <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Pick Your Battle</h1>
            <p className="text-zinc-400 text-sm mt-1">Pass all challenges to unlock the next level.</p>

            {/* Level selector */}
            <div className="mt-6 flex gap-2 overflow-x-auto -mx-5 px-5 pb-2" data-testid="level-selector">
                {levels.map((l) => (
                    <button
                        key={l.level}
                        onClick={() => setActive(l.level)}
                        disabled={!l.unlocked}
                        data-testid={`level-tab-${l.level}`}
                        className={`shrink-0 w-20 h-20 flex flex-col items-center justify-center transition-all ${
                            active === l.level
                                ? "surface-2 glow-green"
                                : l.unlocked
                                  ? "surface-1 hover:border-neon-green"
                                  : "surface-1 opacity-40"
                        }`}
                    >
                        {!l.unlocked ? (
                            <Lock size={16} className="text-zinc-500" />
                        ) : l.completed ? (
                            <Check size={16} className="text-neon-green" />
                        ) : (
                            <Flame size={16} className="text-neon-orange" />
                        )}
                        <span className="font-display font-black text-2xl mt-1 leading-none">{l.level}</span>
                        <span className="text-[9px] uppercase tracking-widest text-zinc-500">Level</span>
                    </button>
                ))}
            </div>

            {/* Active level details */}
            {activeLevel && (
                <motion.div
                    key={active}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6"
                >
                    <div className="surface-2 p-4 mb-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="label-cap text-xs text-neon-green">Level {activeLevel.level}</div>
                                <div className="font-display font-black text-2xl uppercase">
                                    {activeLevel.completed ? "CONQUERED" : activeLevel.unlocked ? "ACTIVE" : "LOCKED"}
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="font-display font-black text-2xl text-neon-green">
                                    {activeLevel.approved_count}/{activeLevel.total}
                                </div>
                                <div className="label-cap text-[10px] text-zinc-500">Approved</div>
                            </div>
                        </div>
                        <div className="mt-3 h-1 bg-charcoal-500">
                            <div
                                className="h-full bg-neon-green transition-all"
                                style={{
                                    width: `${(activeLevel.approved_count / Math.max(1, activeLevel.total)) * 100}%`,
                                    boxShadow: "0 0 8px #39FF14",
                                }}
                            />
                        </div>
                    </div>

                    <div className="space-y-3">
                        {activeLevel.challenges.map((c, i) => (
                            <motion.div
                                key={c.id}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.06 }}
                            >
                                <Link
                                    to={`/app/challenges/${c.id}`}
                                    data-testid={`challenge-card-${c.id}`}
                                    className={`block surface-1 p-4 transition-all ${
                                        activeLevel.unlocked
                                            ? "hover:border-neon-green"
                                            : "opacity-50 pointer-events-none"
                                    }`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className="w-16 h-16 surface-3 flex flex-col items-center justify-center">
                                            <span className="font-display font-black text-2xl text-white leading-none">
                                                {c.reps_required}
                                            </span>
                                            <span className="text-[9px] uppercase tracking-widest text-zinc-500 mt-1">
                                                {c.exercise_type === "plank" ? "sec" : "reps"}
                                            </span>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="font-display font-bold uppercase tracking-tight text-base leading-tight">
                                                    {c.title}
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    {c.is_weighted && (
                                                        <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 text-neon-orange bg-neon-orange/10 border border-neon-orange/40">
                                                            Iron
                                                        </span>
                                                    )}
                                                    <DiffBadge d={c.difficulty} />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 mt-2 text-xs text-zinc-400">
                                                <span className="flex items-center gap-1">
                                                    <Clock size={11} /> {c.time_limit_seconds}s
                                                </span>
                                                <span className="text-neon-green font-bold uppercase tracking-wider">
                                                    +{c.xp_reward} XP
                                                </span>
                                            </div>
                                            {c.is_locked && c.required_items?.length > 0 && (
                                                <div
                                                    className="mt-2 text-[10px] uppercase tracking-wider text-neon-orange flex items-center gap-1"
                                                    data-testid={`challenge-locked-${c.id}`}
                                                >
                                                    🔒 Unlock with {c.required_items[0].name}
                                                    {c.required_items.length > 1 ? ` (+${c.required_items.length - 1})` : ""}
                                                </div>
                                            )}
                                        </div>
                                        <ChevronRight size={18} className="text-zinc-500" />
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            )}
        </div>
    );
}

function DiffBadge({ d }) {
    const map = {
        easy: { c: "#39FF14", l: "Easy" },
        medium: { c: "#F59E0B", l: "Medium" },
        hard: { c: "#FF3B30", l: "Hard" },
        elite: { c: "#FF2424", l: "Elite" },
    };
    const x = map[d] || map.easy;
    return (
        <span
            className="text-[9px] uppercase tracking-widest font-bold px-2 py-1 shrink-0"
            style={{ color: x.c, border: `1px solid ${x.c}40`, background: `${x.c}10` }}
        >
            {x.l}
        </span>
    );
}
